import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import TagsInput from "../FormBuilder/InputField/TagsInput";
import { InviteUser, sendSurvey } from "../../controllers/APIController";
import { addUsersToInvitation } from "../../utils/helper";

/**
 * InviteModal Component
 * Modal for inviting new team members to the organization
 * Handles email validation and invitation submission
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onOpenChange - Function to handle modal open/close state
 * @param {Array} props.UserData - Array of existing users in the organization
 * @param {Array} props.invitationList - Array of emails that already have pending invitations
 * @returns {JSX.Element} Invitation modal interface
 */
const InviteModal = ({ isOpen, onOpenChange, UserData, invitationList }) => {
  // Component state
  const [tags, setTags] = useState([]);
  const [validationError, setValidationError] = useState("");

  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const userId = userLogged && userLogged.user.id;

  // Email validation regex pattern
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Handle invitation submission
   * Validates input and sends invitations to the specified emails
   * @param {Function} onClose - Function to close the modal
   */
  const submitHandler = async (onClose) => {
    if (tags.length === 0) {
      setValidationError("Please enter an email");
      return;
    }

    try {
      await addUsersToInvitation(tags, orgInfo, userId);
      setTags([]);
      onClose();
    } catch (error) {
      console.error("Error sending invitations:", error);
      setValidationError("Failed to send invitations. Please try again.");
    }
  };

  /**
   * Handle modal open/close state changes
   * Clears validation errors and tags when modal is closed
   * @param {boolean} isOpen - Whether the modal should be open
   */
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setValidationError("");
      setTags([]);
    }
    onOpenChange(isOpen);
  };

  /**
   * Validate email input
   * Checks for duplicates, existing users, and valid email format
   * @param {Array} tags - Current list of email tags
   * @param {string} text - Email text to validate
   * @param {Function} setValidateTags - Function to set validation error message
   * @returns {boolean} Whether the email is valid
   */
  const validateEmail = (tags, text, setValidateTags) => {
    let isValid = true;
    const emailExists = UserData && UserData.some((user) => user.email === text);

    if (tags.includes(text)) {
      setValidateTags("Entered email already exists in invite list.");
      isValid = false;
    } else if (emailExists) {
      setValidateTags("Entered email already exists.");
      isValid = false;
    } else if (invitationList.includes(text)) {
      setValidateTags("Invitation already sent to mentioned email.");
      isValid = false;
    } else if (!emailRegex.test(text)) {
      setValidateTags("Enter a valid email.");
      isValid = false;
    } else {
      setValidateTags(null);
    }
    
    return isValid;
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <>
            {/* Modal Header */}
            <ModalHeader className="flex flex-col gap-1">
              Invite your team to your Audyt
            </ModalHeader>
            
            {/* Modal Body - Email Input */}
            <ModalBody className="mb-4">
              <TagsInput
                tags={tags}
                setTags={setTags}
                IntialvalidationError={validationError}
                ValidatonFun={validateEmail}
              />
            </ModalBody>
            
            {/* Modal Footer - Action Buttons */}
            <ModalFooter>
              <Button 
                variant="ghost" 
                className="text-slate-600 hover:text-slate-900 px-4" 
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
                onClick={() => submitHandler(onClose)}
              >
                Send
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default InviteModal;
